import type { Data } from '@repo/strapi'
import { useTranslations } from 'next-intl'
import AppLink from '@/components/elementary/AppLink'
import { Tag } from '@/components/elementary/Tag'
import { BasicImage } from '@/components/page-builder/components/BasicImage'
import { Paragraph } from '@/components/typography/Paragraph'
import { cn } from '@/lib/styles'
import styles from './NavbarMenuLink.module.css'

interface NavbarMenuLinkProps {
	readonly link: Data.Component<'layout-utilities.navbar-links'>
	readonly isDropDownItem?: boolean
	readonly stackIcon?: boolean
	readonly testId?: string
	onClick?: () => void
}

export function NavbarMenuLink({
	link,
	isDropDownItem = false,
	stackIcon = false,
	testId,
	onClick,
}: NavbarMenuLinkProps) {
	const t = useTranslations('Navbar')
	const shouldStackIcon = stackIcon && link.icon

	return (
		<AppLink
			variant="primaryLink"
			size="none"
			href={link.href ?? ''}
			key={link.id}
			openExternalInNewTab={!!link.newTab}
			className="flex flex-col gap-2 w-fit items-start"
			data-test-id={testId}
			onClick={onClick}
		>
			<div
				className={cn(
					'flex gap-2 w-fit flex-row items-center',
					shouldStackIcon && 'lg:flex-col lg:items-start',
				)}
			>
				{link.icon && (
					<div
						className={cn(
							'flex justify-center shrink-0 size-8 items-center p-1.25 border border-neutral-light-400 rounded',
							link.hasPeachIconBackground ? 'bg-peach-200' : 'bg-transparent',
						)}
					>
						<div className="relative size-6 shrink-0">
							<BasicImage component={link?.icon} className="object-contain" fill ignoreMediaSize />
						</div>
					</div>
				)}

				<div className={cn('flex', isDropDownItem ? 'flex-col' : 'gap-1.5')}>
					{link?.title && (
						<Paragraph
							fontSize="bodyRegularMedium"
							className={cn(
								'text-inherit',
								isDropDownItem
									? styles['nav-content-dropdown-link']
									: styles['nav-content-menu-link'],
							)}
						>
							{link.title}
							{!isDropDownItem && link?.newTag && (
								<Tag name={t('newBadge')} variant="green-success" size="small" className="ml-1.5" />
							)}
						</Paragraph>
					)}

					{link?.description && !isDropDownItem && (
						<Paragraph
							className={cn('text-inherit whitespace-nowrap', styles['nav-content-menu-link'])}
						>
							{link.description}
						</Paragraph>
					)}
				</div>
			</div>

			{link?.description && isDropDownItem && (
				<Paragraph fontSize="bodyRegularMedium">{link.description}</Paragraph>
			)}
		</AppLink>
	)
}
